import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Upload, 
  Button, 
  message, 
  Card, 
  Typography, 
  Space, 
  Divider, 
  Row, 
  Col, 
  Select, 
  DatePicker, 
  InputNumber, 
  Tag, 
  Tabs, 
  Spin,
  Timeline,
  Avatar,
  Badge,
  Alert,
  Descriptions
} from 'antd';
import { 
  UploadOutlined, 
  FileTextOutlined, 
  CheckCircleOutlined, 
  BookOutlined, 
  ToolOutlined, 
  PlusOutlined, 
  DeleteOutlined,
  MessageOutlined,
  UserOutlined,
  BuildOutlined
} from '@ant-design/icons';
import { FileUploadService } from '../../services/FileUploadService';
import { validateFile, getAllowedFileTypesDisplay, formatFileSize } from '../../config/fileUpload';
import { API_BASE_URL } from '../../services/ApiUrls';
import DashboardService from '../../services/dashboard/DashboardService';
import ApplicationMessageService, { ApplicationMessageDTO } from '../../services/ApplicationMessageService';
import moment from 'moment';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface EditApplicationFormProps {
  visible: boolean;
  application: any;
  onCancel: () => void;
  onSubmit: (updatedData: any) => void;
}

interface ApplicationFormData {
  coverLetter: string;
  additionalNotes: string;
  resumeUrl?: string;
  additionalDocuments?: any[];
  userEducations?: UserEducationDTO[];
  userSkills?: UserSkillDTO[];
  resubmitComments?: string;
}

interface UserEducationDTO {
  id?: number;
  institutionName: string;
  degree: string;
  fieldOfStudy: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  grade: string;
  description: string;
}

interface UserSkillDTO {
  id?: number;
  skillName: string;
  skillLevel: string;
  yearsOfExperience: number;
  category: string;
}

// Internship Details Component
const InternshipDetailsSection: React.FC<{ application: any }> = ({ application }) => {
  return (
    <Card 
      title={
        <Space>
          <BuildOutlined style={{ color: '#1890ff' }} />
          <span>Internship Details</span>
        </Space>
      }
      style={{ marginBottom: '20px' }}
      size="small"
    >
      <Descriptions column={2} size="small">
        <Descriptions.Item label="Position">
          <Text strong>{application.internshipName || application.internshipTitle || 'N/A'}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Organization">
          <Text>{application.organizationName || 'N/A'}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Location">
          <Text>{application.internshipLocation || application.location || 'Remote'}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Work Mode">
          <Text>{application.internshipWorkMode || application.workMode || 'Remote'}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Duration">
          <Text>{application.internshipDuration || 'Not specified'}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Stipend">
          <Text style={{ color: application.internshipStipend > 0 ? '#52c41a' : '#999' }}>
            {application.internshipStipend > 0 ? `₹${application.internshipStipend.toLocaleString()}` : 'Unpaid'}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Application Status">
          <Tag color={application.applicationStatus?.toLowerCase().includes('need') ? 'orange' : 'blue'}>
            {application.applicationStatus}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Applied Date">
          <Text>{application.appliedAt ? moment(application.appliedAt).format('DD MMM YYYY') : 'N/A'}</Text>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};

// Conversation Messages Component
const ConversationSection: React.FC<{ 
  application: any; 
  messages: ApplicationMessageDTO[]; 
  loading: boolean;
  onSendMessage: (message: string) => void;
}> = ({ application, messages, loading, onSendMessage }) => {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await onSendMessage(newMessage);
      setNewMessage('');
      message.success('Message sent successfully!');
    } catch (error) {
      message.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getSenderIcon = (senderRole: string) => {
    return senderRole === 'STUDENT' ? <UserOutlined /> : <BuildOutlined />;
  };

  const getSenderColor = (senderRole: string) => {
    return senderRole === 'STUDENT' ? '#1890ff' : '#52c41a';
  };

  return (
    <Card 
      title={
        <Space>
          <MessageOutlined style={{ color: '#1890ff' }} />
          <span>Conversation History</span>
          <Badge count={messages.filter(m => !m.isRead && m.senderRole === 'ORGANIZATION').length} />
        </Space>
      }
      style={{ marginBottom: '20px' }}
      size="small"
    >
      <Spin spinning={loading}>
        {messages.length === 0 ? (
          <Alert
            message="No messages yet"
            description="Start a conversation with the organization about your application."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        ) : (
          <Timeline
            items={messages.map((msg) => ({
              dot: (
                <Avatar 
                  size="small" 
                  icon={getSenderIcon(msg.senderRole)} 
                  style={{ backgroundColor: getSenderColor(msg.senderRole) }}
                />
              ),
              children: (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <Space>
                      <Text strong style={{ color: getSenderColor(msg.senderRole) }}>
                        {msg.senderName} ({msg.senderRole})
                      </Text>
                      {!msg.isRead && msg.senderRole === 'ORGANIZATION' && (
                        <Tag color="red">New</Tag>
                      )}
                    </Space>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {moment(msg.createdAt).format('DD MMM YYYY, HH:mm')}
                    </Text>
                  </div>
                  <div style={{ 
                    background: msg.senderRole === 'STUDENT' ? '#f6ffed' : '#f0f9ff',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${msg.senderRole === 'STUDENT' ? '#b7eb8f' : '#91d5ff'}`
                  }}>
                    <Text>{msg.messageContent}</Text>
                  </div>
                </div>
              )
            }))}
          />
        )}
        
        <Divider />
        
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onPressEnter={handleSendMessage}
            disabled={sending}
          />
          <Button 
            type="primary" 
            icon={<MessageOutlined />}
            onClick={handleSendMessage}
            loading={sending}
            disabled={!newMessage.trim()}
          >
            Send
          </Button>
        </Space.Compact>
      </Spin>
    </Card>
  );
};

// Education Section Component
const EducationSection: React.FC<{ detailedData: any; form: any }> = ({ detailedData, form }) => {
  const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());

  const handleEditRow = (index: number) => {
    setEditingRows(prev => new Set(prev).add(index));
  };

  const handleCancelRow = (index: number) => {
    setEditingRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  const handleSaveRow = async (index: number) => {
    setSavingRows(prev => new Set(prev).add(index));
    try {
      // Get the form values for this specific row
      const formValues = form.getFieldsValue();
      const educationData = formValues.userEducations[index];
      
      // Here you would typically save the education data to the backend
      console.log('Saving education data:', educationData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success('Education saved successfully!');
      setEditingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    } catch (error) {
      message.error('Failed to save education');
    } finally {
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  return (
    <Form.List name="userEducations">
      {(fields, { add, remove }) => (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0 }}>
              <BookOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
              Education Details
            </Title>
            <Button 
              type="dashed" 
              icon={<PlusOutlined />} 
              onClick={() => add()}
              size="small"
            >
              Add Education
            </Button>
          </div>

          {fields.map(({ key, name, ...restField }) => {
            const isEditing = editingRows.has(name);
            const isSaving = savingRows.has(name);
            
            return (
              <Card key={key} size="small" style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Text strong>Education #{name + 1}</Text>
                  <Space>
                    {!isEditing ? (
                      <>
                        <Button 
                          type="primary" 
                          size="small"
                          onClick={() => handleEditRow(name)}
                        >
                          Edit
                        </Button>
                        <Button 
                          type="text" 
                          danger 
                          icon={<DeleteOutlined />} 
                          onClick={() => remove(name)}
                          size="small"
                        />
                      </>
                    ) : (
                      <>
                        <Button 
                          type="primary" 
                          size="small"
                          loading={isSaving}
                          onClick={() => handleSaveRow(name)}
                        >
                          Save
                        </Button>
                        <Button 
                          size="small"
                          onClick={() => handleCancelRow(name)}
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </Space>
                </div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'institutionName']}
                      label="Institution Name"
                      rules={isEditing ? [{ required: true, message: 'Please enter institution name!' }] : []}
                    >
                      <Input 
                        placeholder="University/College name" 
                        disabled={!isEditing}
                        style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'degree']}
                      label="Degree"
                      rules={isEditing ? [{ required: true, message: 'Please select degree!' }] : []}
                    >
                      <Select 
                        placeholder="Select degree" 
                        disabled={!isEditing}
                        style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                      >
                        <Option value="BACHELOR">Bachelor's</Option>
                        <Option value="MASTER">Master's</Option>
                        <Option value="PHD">PhD</Option>
                        <Option value="DIPLOMA">Diploma</Option>
                        <Option value="CERTIFICATE">Certificate</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'fieldOfStudy']}
                      label="Field of Study"
                      rules={isEditing ? [{ required: true, message: 'Please enter field of study!' }] : []}
                    >
                      <Input 
                        placeholder="Computer Science, Engineering, etc." 
                        disabled={!isEditing}
                        style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      {...restField}
                      name={[name, 'grade']}
                      label="Grade/CGPA"
                    >
                      <Input 
                        placeholder="8.5/10 or A+" 
                        disabled={!isEditing}
                        style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'startDate']}
                      label="Start Date"
                      rules={isEditing ? [{ required: true, message: 'Please select start date!' }] : []}
                    >
                      <DatePicker 
                        style={{ width: '100%', backgroundColor: isEditing ? 'white' : '#f5f5f5' }} 
                        disabled={!isEditing}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'endDate']}
                      label="End Date"
                    >
                      <DatePicker 
                        style={{ width: '100%', backgroundColor: isEditing ? 'white' : '#f5f5f5' }} 
                        disabled={!isEditing}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'isCurrent']}
                      label="Currently Studying"
                      valuePropName="checked"
                    >
                      <Select 
                        placeholder="Select" 
                        disabled={!isEditing}
                        style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                      >
                        <Option value={true}>Yes</Option>
                        <Option value={false}>No</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  {...restField}
                  name={[name, 'description']}
                  label="Description"
                >
                  <TextArea 
                    rows={3} 
                    placeholder="Additional details about your education..." 
                    disabled={!isEditing}
                    style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                  />
                </Form.Item>
              </Card>
            );
          })}
        </>
      )}
    </Form.List>
  );
};

// Skills Section Component
const SkillsSection: React.FC<{ detailedData: any; form: any }> = ({ detailedData, form }) => {
  const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());

  const handleEditRow = (index: number) => {
    setEditingRows(prev => new Set(prev).add(index));
  };

  const handleCancelRow = (index: number) => {
    setEditingRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  const handleSaveRow = async (index: number) => {
    setSavingRows(prev => new Set(prev).add(index));
    try {
      // Get the form values for this specific row
      const formValues = form.getFieldsValue();
      const skillData = formValues.userSkills[index];
      
      // Here you would typically save the skill data to the backend
      console.log('Saving skill data:', skillData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success('Skill saved successfully!');
      setEditingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    } catch (error) {
      message.error('Failed to save skill');
    } finally {
      setSavingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  return (
    <Form.List name="userSkills">
      {(fields, { add, remove }) => (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Title level={5} style={{ margin: 0 }}>
              <ToolOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
              Skills & Experience
            </Title>
            <Button 
              type="dashed" 
              icon={<PlusOutlined />} 
              onClick={() => add()}
              size="small"
            >
              Add Skill
            </Button>
          </div>

          {fields.map(({ key, name, ...restField }) => {
            const isEditing = editingRows.has(name);
            const isSaving = savingRows.has(name);
            
            return (
              <Card key={key} size="small" style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <Text strong>Skill #{name + 1}</Text>
                  <Space>
                    {!isEditing ? (
                      <>
                        <Button 
                          type="primary" 
                          size="small"
                          onClick={() => handleEditRow(name)}
                        >
                          Edit
                        </Button>
                        <Button 
                          type="text" 
                          danger 
                          icon={<DeleteOutlined />} 
                          onClick={() => remove(name)}
                          size="small"
                        />
                      </>
                    ) : (
                      <>
                        <Button 
                          type="primary" 
                          size="small"
                          loading={isSaving}
                          onClick={() => handleSaveRow(name)}
                        >
                          Save
                        </Button>
                        <Button 
                          size="small"
                          onClick={() => handleCancelRow(name)}
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </Space>
                </div>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'skillName']}
                      label="Skill Name"
                      rules={isEditing ? [{ required: true, message: 'Please enter skill name!' }] : []}
                    >
                      <Input 
                        placeholder="e.g., Java, React, Python" 
                        disabled={!isEditing}
                        style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'skillLevel']}
                      label="Skill Level"
                      rules={isEditing ? [{ required: true, message: 'Please select skill level!' }] : []}
                    >
                      <Select 
                        placeholder="Select level" 
                        disabled={!isEditing}
                        style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                      >
                        <Option value="BEGINNER">Beginner</Option>
                        <Option value="INTERMEDIATE">Intermediate</Option>
                        <Option value="ADVANCED">Advanced</Option>
                        <Option value="EXPERT">Expert</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'yearsOfExperience']}
                      label="Years of Experience"
                      rules={isEditing ? [{ required: true, message: 'Please enter years of experience!' }] : []}
                    >
                      <InputNumber 
                        min={0} 
                        max={20} 
                        style={{ width: '100%', backgroundColor: isEditing ? 'white' : '#f5f5f5' }} 
                        placeholder="0"
                        disabled={!isEditing}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  {...restField}
                  name={[name, 'category']}
                  label="Category"
                  rules={isEditing ? [{ required: true, message: 'Please select category!' }] : []}
                >
                  <Select 
                    placeholder="Select category" 
                    disabled={!isEditing}
                    style={{ backgroundColor: isEditing ? 'white' : '#f5f5f5' }}
                  >
                    <Option value="PROGRAMMING">Programming</Option>
                    <Option value="FRAMEWORK">Framework</Option>
                    <Option value="DATABASE">Database</Option>
                    <Option value="TOOL">Tool</Option>
                    <Option value="LANGUAGE">Language</Option>
                    <Option value="SOFT_SKILL">Soft Skill</Option>
                  </Select>
                </Form.Item>
              </Card>
            );
          })}
        </>
      )}
    </Form.List>
  );
};

const EditApplicationForm: React.FC<EditApplicationFormProps> = ({
  visible,
  application,
  onCancel,
  onSubmit
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [detailedApplicationData, setDetailedApplicationData] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [messages, setMessages] = useState<ApplicationMessageDTO[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Fetch detailed application data including eligibility and skills
  const fetchDetailedApplicationData = async (applicationId: string) => {
    try {
      setLoadingDetails(true);
      console.log('🔍 [EditApplicationForm] Fetching detailed application data for ID:', applicationId);
      
      const response = await DashboardService.getApplicationDetails(applicationId);
      
      if (response.status === 'success' && response.data) {
        console.log('✅ [EditApplicationForm] Detailed application data fetched:', response.data);
        setDetailedApplicationData(response.data);
        return response.data;
      } else {
        console.error('❌ [EditApplicationForm] Failed to fetch detailed application data:', response);
        message.error('Failed to load application details');
        return null;
      }
    } catch (error: any) {
      console.error('❌ [EditApplicationForm] Error fetching detailed application data:', error);
      message.error('Failed to load application details');
      return null;
    } finally {
      setLoadingDetails(false);
    }
  };

  // Fetch conversation messages
  const fetchMessages = async (applicationId: string) => {
    try {
      setLoadingMessages(true);
      console.log('💬 [EditApplicationForm] Fetching messages for application:', applicationId);
      
      const response = await ApplicationMessageService.getMessagesWithThreading(applicationId);
      
      if (response.status === 'success' && response.data) {
        console.log('✅ [EditApplicationForm] Messages fetched:', response.data);
        setMessages(response.data);
      } else {
        console.error('❌ [EditApplicationForm] Failed to fetch messages:', response);
        setMessages([]);
      }
    } catch (error: any) {
      console.error('❌ [EditApplicationForm] Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Send a new message
  const handleSendMessage = async (messageText: string) => {
    if (!application?.applicationId) return;
    
    try {
      const response = await ApplicationMessageService.sendMessage({
        applicationId: application.applicationId,
        messageContent: messageText,
        messageType: 'TEXT'
      });
      
      if (response.status === 'success') {
        // Refresh messages
        await fetchMessages(application.applicationId);
      }
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send message');
    }
  };

  // Initialize form with existing application data
  useEffect(() => {
    if (visible && application) {
      console.log('📝 [EditApplicationForm] Initializing form with application data:', application);
      
      // Set initial form values
      form.setFieldsValue({
        coverLetter: application.coverLetter || '',
        additionalNotes: application.additionalNotes || '',
        resubmitComments: ''
      });

      // Initialize file list if resume exists
      if (application.resumeUrl) {
        setFileList([{
          uid: '-1',
          name: 'Current Resume',
          status: 'done',
          url: application.resumeUrl,
        }]);
      } else {
        setFileList([]);
      }

      // Fetch detailed application data and messages
      const applicationId = application.applicationId || application.id;
      if (applicationId) {
        fetchDetailedApplicationData(applicationId.toString());
        fetchMessages(applicationId.toString());
      }
    } else {
      // Reset form when modal closes
      form.resetFields();
      setFileList([]);
      setDetailedApplicationData(null);
      setMessages([]);
    }
  }, [visible, application, form]);

  const handleFileUpload = async (file: any) => {
    console.log('📤 [EditApplicationForm] Uploading file:', file.name);
    
    // Validate file
    const validationResult = validateFile(file);
    if (!validationResult.isValid) {
      message.error(validationResult.error || 'Invalid file');
      return false;
    }

    setUploading(true);
    try {
      const response = await FileUploadService.uploadFile(file, {
        uploadedFor: 'INTERNSHIP_APPLICATION',
        fileType: 'RESUME',
        referenceId: application?.applicationId?.toString() || application?.id?.toString(),
        metadata: JSON.stringify({
          applicationId: application?.applicationId || application?.id,
          documentType: 'RESUME',
          isEdit: true
        })
      });
      
      if (response && response.fileUploadId) {
        console.log('✅ [EditApplicationForm] File uploaded successfully:', response);
        message.success(`${file.name} uploaded successfully`);
        
        // Update file list
        const newFileList = [...fileList, {
          uid: file.uid,
          name: response.fileName || file.name,
          status: 'done',
          url: `${API_BASE_URL}/files/${response.fileUploadId}`,
          response: response,
        }];
        setFileList(newFileList);
        
        return false; // Prevent default upload behavior
      } else {
        throw new Error('Upload failed - invalid response');
      }
    } catch (error: any) {
      console.error('❌ [EditApplicationForm] File upload error:', error);
      message.error(`Failed to upload ${file.name}: ${error.message}`);
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleFileRemove = (file: any) => {
    console.log('🗑️ [EditApplicationForm] Removing file:', file.name);
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
  };

  const handleSubmit = async (values: ApplicationFormData) => {
    console.log('🚀 [EditApplicationForm] handleSubmit called with values:', values);
    console.log('🔍 [EditApplicationForm] Form validation check...');
    
    // Check if required fields are filled
    if (!values.resubmitComments || values.resubmitComments.trim().length < 20) {
      console.error('❌ [EditApplicationForm] Validation failed: resubmitComments is required and must be at least 20 characters');
      console.error('❌ [EditApplicationForm] Current resubmitComments length:', values.resubmitComments?.length || 0);
      return; // This will prevent the API call
    }
    
    setLoading(true);
    try {
      console.log('🚀 [EditApplicationForm] Starting resubmit process...');
      console.log('📋 [EditApplicationForm] Form values:', values);
      console.log('📁 [EditApplicationForm] File list:', fileList);
      console.log('📄 [EditApplicationForm] Application data:', application);
      
      let resumeUrl = application.resumeUrl; // Default to existing resume

      if (fileList.length > 0 && fileList[0].status === 'done' && fileList[0].response) {
        // New file uploaded
        resumeUrl = `${API_BASE_URL}/files/${fileList[0].response.fileUploadId}`;
        console.log('📎 [EditApplicationForm] New resume URL:', resumeUrl);
      } else if (fileList.length === 0) {
        // Resume removed
        resumeUrl = null;
        console.log('🗑️ [EditApplicationForm] Resume removed');
      }
      
      const resubmitData = {
        coverLetter: values.coverLetter,
        additionalNotes: values.additionalNotes,
        resubmitComments: values.resubmitComments,
        resumeUrl: resumeUrl,
        additionalDocuments: values.additionalDocuments || [],
        userEducations: detailedApplicationData?.userEducations || [],
        userSkills: detailedApplicationData?.userSkills || [],
        applicationStatus: 'APPLIED',
        resubmittedAt: new Date().toISOString()
      };
      
      console.log('📦 [EditApplicationForm] Resubmitting application with data:', resubmitData);
      
      // Use the new resubmit endpoint
      const applicationId = application.applicationId || application.id;
      console.log('🆔 [EditApplicationForm] Application ID:', applicationId);
      console.log('🆔 [EditApplicationForm] Application ID as string:', applicationId.toString());
      
      console.log('📡 [EditApplicationForm] Making API call to resubmit endpoint...');
      const response = await DashboardService.resubmitApplication(applicationId.toString(), resubmitData);
      console.log('✅ [EditApplicationForm] API response received:', response);
      
      if (response.status === 'success') {
        // Send a system message to notify about the resubmission
        try {
          await ApplicationMessageService.sendMessage({
            applicationId: applicationId.toString(),
            messageContent: `Application has been resubmitted with updated information. Status changed to APPLIED.`,
            messageType: 'SYSTEM'
          });
        } catch (messageError) {
          console.warn('Failed to send system message:', messageError);
          // Don't fail the resubmission if message sending fails
        }
        
        message.success('Application resubmitted successfully! The organization will be notified to review your updated application.');
        
        // Notify parent component to refresh the applications list
        if (onSubmit) {
          console.log('🔄 [EditApplicationForm] Notifying parent component to refresh applications list');
          onSubmit(resubmitData);
        } else {
          console.warn('⚠️ [EditApplicationForm] No onSubmit callback provided - applications list may not refresh');
        }
        
        onCancel(); // Close the modal
      } else {
        throw new Error(response.message || 'Failed to resubmit application');
      }
    } catch (error: any) {
      console.error('❌ [EditApplicationForm] Submit error:', error);
      message.error(error.message || 'Failed to resubmit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setDetailedApplicationData(null);
    setMessages([]);
    onCancel();
  };

  if (!application) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileTextOutlined style={{ color: '#1890ff' }} />
          <span>Edit Application & Respond to Feedback</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={1200}
      centered
      destroyOnClose
      style={{ top: 20 }}
    >
      <Spin spinning={loadingDetails}>
        <div style={{ padding: '20px 0' }}>
          {/* Internship Details Section */}
          <InternshipDetailsSection application={application} />
          
          {/* Conversation Section */}
          <ConversationSection 
            application={application}
            messages={messages}
            loading={loadingMessages}
            onSendMessage={handleSendMessage}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => {
              console.log('📝 [EditApplicationForm] Form onFinish triggered with values:', values);
              handleSubmit(values);
            }}
            onFinishFailed={(errorInfo) => {
              console.error('❌ [EditApplicationForm] Form validation failed:', errorInfo);
            }}
            initialValues={{
              coverLetter: application.coverLetter || '',
              additionalNotes: application.additionalNotes || '',
              resubmitComments: '',
              userEducations: detailedApplicationData?.userEducations?.map((edu: UserEducationDTO) => ({
                ...edu,
                startDate: edu.startDate ? moment(edu.startDate) : null,
                endDate: edu.endDate ? moment(edu.endDate) : null,
              })) || [],
              userSkills: detailedApplicationData?.userSkills || [],
            }}
          >
            <Tabs defaultActiveKey="basicDetails" style={{ marginBottom: 20 }}>
              <TabPane tab="Application Details" key="basicDetails">
                {/* Cover Letter */}
                <Form.Item
                  label="Cover Letter"
                  name="coverLetter"
                  rules={[
                    { required: true, message: 'Please provide a cover letter!' },
                    { min: 50, message: 'Cover letter must be at least 50 characters long!' },
                    { max: 2000, message: 'Cover letter must not exceed 2000 characters!' }
                  ]}
                  extra="Explain why you're interested in this internship and how it aligns with your career goals."
                >
                  <TextArea
                    rows={6}
                    placeholder="Write your cover letter here..."
                    showCount
                    maxLength={2000}
                  />
                </Form.Item>

                {/* Additional Notes */}
                <Form.Item
                  label="Additional Notes"
                  name="additionalNotes"
                  extra="Any additional information you'd like to share with the organization."
                >
                  <TextArea
                    rows={4}
                    placeholder="Any additional notes or information..."
                    showCount
                    maxLength={1000}
                  />
                </Form.Item>

                {/* Resume Upload */}
                <Form.Item
                  label="Resume"
                  extra={`Upload your resume. Allowed formats: ${getAllowedFileTypesDisplay()}. Max size: ${formatFileSize(10485760)}`}
                >
                  <Upload
                    fileList={fileList}
                    beforeUpload={handleFileUpload}
                    onRemove={handleFileRemove}
                    accept=".pdf,.doc,.docx"
                    maxCount={1}
                    disabled={uploading}
                  >
                    <Button icon={<UploadOutlined />} loading={uploading}>
                      {uploading ? 'Uploading...' : 'Upload Resume'}
                    </Button>
                  </Upload>
                </Form.Item>
              </TabPane>

              <TabPane tab="Education" key="education">
                <EducationSection detailedData={detailedApplicationData} form={form} />
              </TabPane>

              <TabPane tab="Skills" key="skills">
                <SkillsSection detailedData={detailedApplicationData} form={form} />
              </TabPane>

              <TabPane tab="Resubmit" key="resubmit">
                <Alert
                  message="Resubmit Application"
                  description="After making your changes, add your response to the organization's feedback below and resubmit your application. The status will change to 'APPLIED'."
                  type="info"
                  showIcon
                  style={{ marginBottom: '20px' }}
                />

                <Form.Item
                  label="Response to Organization Feedback"
                  name="resubmitComments"
                  rules={[
                    { required: true, message: 'Please provide your response to the organization feedback!' },
                    { min: 20, message: 'Response must be at least 20 characters long!' },
                    { max: 1000, message: 'Response must not exceed 1000 characters!' }
                  ]}
                  extra="Explain what changes you've made based on the organization's feedback."
                >
                  <TextArea
                    rows={6}
                    placeholder="Dear Organization, I have updated my application based on your feedback..."
                    showCount
                    maxLength={1000}
                  />
                </Form.Item>

                <Alert
                  message="Important Notice"
                  description="Once you resubmit, your application status will change to 'APPLIED' and the organization will be notified to review your updated application."
                  type="warning"
                  showIcon
                />
              </TabPane>
            </Tabs>

            {/* Form Actions */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px',
              padding: '20px 0',
              borderTop: '1px solid #f0f0f0',
              marginTop: '20px'
            }}>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<CheckCircleOutlined />}
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => {
                  console.log('🖱️ [EditApplicationForm] Resubmit button clicked!');
                  console.log('🖱️ [EditApplicationForm] Current form values:', form.getFieldsValue());
                }}
              >
                {loading ? 'Resubmitting...' : 'Resubmit Application'}
              </Button>
            </div>
          </Form>
        </div>
      </Spin>
    </Modal>
  );
};

export default EditApplicationForm;
